import { getLineItemPaidPrice } from "@babackapp/calculations/get-line-item-paid-price";
import { getReturnRequestRefundShippingCost } from "@babackapp/calculations/get-return-request-refund-shipping-cost";
import { ReturnRequestLineItemDto } from "@babackapp/private-api-client/types";
import { useMemo } from "react";
import { useCurrentReturnRequest } from "../../utils/current-return-request-context";
import useLineItemsState from "./use-line-items-state-context";
import { useRefundLineItems } from "./use-refund-line-items-context";

type UseTotalToRefundOptions = {
  withCharges?: boolean;
  chargeForRefund?: boolean;
  chargeForInitialShipping?: boolean;
  forceRefundInitialShipping?: boolean;
};

export function useRefundSelectedArticles({
  withCharges = false,
  chargeForRefund = false,
  chargeForInitialShipping = false,
  forceRefundInitialShipping = false,
}: UseTotalToRefundOptions = {}) {
  const returnRequest = useCurrentReturnRequest();
  const { items: draftLineItems } = useRefundLineItems();
  const { lineItemsState } = useLineItemsState();

  const amount = useMemo(() => {
    let _amount = draftLineItems.reduce(
      (prev: number, li: ReturnRequestLineItemDto) => {
        if (
          li.resolution !== "refund" ||
          lineItemsState[li.line_item_id].total.selectedQuantity <= 0
        )
          return prev;
        const itemState = lineItemsState[li.line_item_id];
        const price = getLineItemPaidPrice({
          lineItem: li,
          quantity: itemState ? itemState.total.selectedQuantity : 0,
        });
        return prev + price;
      },
      0,
    );

    if (withCharges) {
      const shippingCost = getReturnRequestRefundShippingCost({
        returnRequest,
        chargeForRefund,
        chargeForInitialShipping,
        forceRefundInitialShipping,
      });
      _amount -= shippingCost;
    }

    return Math.round(_amount * 100) / 100;
  }, [
    draftLineItems,
    returnRequest,
    lineItemsState,
    withCharges,
    chargeForRefund,
    chargeForInitialShipping,
    forceRefundInitialShipping,
  ]);

  return amount === 0 ? null : amount; // Avoids displaying 0.00
}
